import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useApiClient } from '../../hooks/useApiClient'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { getStatusColors } from '../../utils/statusColorMap'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import PlanoMapper from '../../components/admin/PlanoMapper'
import EspacioTarifasManager from '../../components/admin/EspacioTarifasManager'
import ImageUploader from '../../components/common/ImageUploader'
import { ActionButton, ConfirmDeleteModal } from '../../components/ui'

interface TipoEspacio {
  id: string
  nombre: string
  descripcion: string
  capacidad_maxima: number
  tarifa_base_tipo: number
  tarifa_base_tipo_multiple?: number
  caracteristicas_requeridas: Record<string, any>
  privado?: boolean  // Si es privado (solo admin) o público (sitio web)
}

interface Espacio {
  id: string
  tipo_espacio_id: string
  codigo: string
  nombre: string
  descripcion: string | null
  ubicacion: string | null
  estado: string
  imagenes: string[]
  tarifa_base_espacio_simple: number | null
  tarifa_base_espacio_multiple: number | null
  caracteristicas: Record<string, any>
  empresa_id: number
  created_at: string
  updated_at: string
  tipo_espacio?: TipoEspacio
  // Posición en cuadrícula
  grid_fila?: number | null
  grid_columna?: number | null
  grid_zona?: string | null
  // Posición en plano visual
  plano_x?: number | null
  plano_y?: number | null
  plano_ancho?: number | null
  plano_alto?: number | null
  plano_radio?: number | null
  plano_vertices?: Array<{ x: number; y: number }> | null
}

interface EspacioForm {
  tipo_espacio_id: string
  codigo: string
  nombre: string
  descripcion: string
  ubicacion: string
  estado: string
  imagenes: string[]
  tarifa_base_espacio_simple: number | null
  tarifa_base_espacio_multiple: number | null
  caracteristicas: Record<string, any>
  // Posición en cuadrícula
  grid_fila: number | null
  grid_columna: number | null
  grid_zona: string
  // Posición en plano visual
  plano_x: number | null
  plano_y: number | null
  plano_ancho: number | null
  plano_alto: number | null
  plano_radio: number | null
  plano_vertices: Array<{ x: number; y: number }> | null
}

const AdminEspacios: React.FC = () => {
  const { permissions, user: usuario } = useAuth()
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()
  const [espacios, setEspacios] = useState<Espacio[]>([])
  const [tiposEspacios, setTiposEspacios] = useState<TipoEspacio[]>([])
  const [showForm, setShowForm] = useState(false)
  const [editingEspacio, setEditingEspacio] = useState<Espacio | null>(null)
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ id: string; nombre: string } | null>(null)

  const hasPermission = (permission: string): boolean => {
    return permissions?.includes(permission) || false
  }

  // Debug: log cuando cambia la empresa
  useEffect(() => {
    // empresa tracking
  }, [usuario?.empresa_activa?.id])
  const [form, setForm] = useState<EspacioForm>({
    tipo_espacio_id: '',
    codigo: '',
    nombre: '',
    descripcion: '',
    ubicacion: '',
    estado: 'disponible',
    imagenes: [],
    tarifa_base_espacio_simple: null,
    tarifa_base_espacio_multiple: null,
    caracteristicas: {},
    // Posición en cuadrícula
    grid_fila: null,
    grid_columna: null,
    grid_zona: '',
    // Posición en plano visual
    plano_x: null,
    plano_y: null,
    plano_ancho: null,
    plano_alto: null,
    plano_radio: null,
    plano_vertices: null
  })

  const { apiClient, loading, error, setLoading, setError } = useApiClient()

  // Función helper para formatear errores de Pydantic
  const formatErrorMessage = (err: any): string => {
    const detail = err.response?.data?.detail
    let errorMsg = 'Error en la operación'

    if (typeof detail === 'string') {
      errorMsg = detail
    } else if (Array.isArray(detail)) {
      // Detail es un array de errores de validación de Pydantic
      errorMsg = detail.map((e: any) => {
        if (typeof e === 'string') return e
        if (e.msg) return `${e.loc?.join('.')} - ${e.msg}`
        return JSON.stringify(e)
      }).join('; ')
    } else if (detail && typeof detail === 'object') {
      errorMsg = JSON.stringify(detail)
    }

    return errorMsg
  }

  // ⭐ Memoizar función para evitar loop infinito en PlanoMapper
  const handleCoordinatesChange = useCallback((coords: {
    plano_x: number | null
    plano_y: number | null
    plano_radius: number | null
    plano_ancho: number | null
    plano_alto: number | null
    plano_vertices: Array<{ x: number; y: number }> | null
  }) => {
    setForm(prev => ({
      ...prev,
      plano_x: coords.plano_x,
      plano_y: coords.plano_y,
      plano_radio: coords.plano_radius,
      plano_ancho: coords.plano_ancho,
      plano_alto: coords.plano_alto,
      plano_vertices: coords.plano_vertices
    }))
  }, [])

  useEffect(() => {
    fetchEspacios()
    fetchTiposEspacios()
  }, [usuario?.empresa_activa?.id])

  const fetchEspacios = async () => {
    try {
      setLoading(true)
      setError(null)

      let url = '/api/admin-protected/espacios'
      const params = new URLSearchParams()
      if (filtroTipo) params.append('tipo_espacio_id', filtroTipo)
      if (filtroEstado) params.append('estado', filtroEstado)

      if (params.toString()) {
        url += `?${params.toString()}`
      }

      const response = await apiClient.get(url)

      // El apiClient ya hace unwrapping automático de respuestas UAU
      const data = response.data || []

      setEspacios(Array.isArray(data) ? data : [])
    } catch (err: any) {
      console.error('Error fetching espacios:', err)
      setError(formatErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const fetchTiposEspacios = async () => {
    try {
      // Obtener todos los tipos de espacios (sin límite de resultados para que aparezcan todos en el select)
      const response = await apiClient.get('/api/admin-protected/tipos-espacios?limite=1000&offset=0')
      const data = response.data || []
      setTiposEspacios(Array.isArray(data) ? data : [])
    } catch (err: any) {
      console.error('Error fetching tipos espacios:', err)
      setTiposEspacios([])
    }
  }


  useEffect(() => {
    fetchEspacios()
  }, [filtroTipo, filtroEstado, usuario?.empresa_activa?.id])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)
      setError(null)

      // Convertir URLs absolutas de vuelta a relativas para enviar al servidor
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      const imagenesRelativas = form.imagenes.map(img =>
        img.startsWith(apiUrl) ? img.replace(apiUrl, '') : img
      )

      // Limpiar coordenadas: no enviar ambos sistemas simultáneamente
      const tieneGridCoordinates = form.grid_fila !== null || form.grid_columna !== null || form.grid_zona
      const tieneMapCoordinates = form.plano_x !== null || form.plano_y !== null || form.plano_ancho !== null || form.plano_alto !== null || form.plano_radio !== null || form.plano_vertices

      let cleanForm = { ...form }

      // Si tiene coordenadas de grid, limpiar coordenadas de plano
      if (tieneGridCoordinates && tieneMapCoordinates) {
        cleanForm = {
          ...cleanForm,
          plano_x: null,
          plano_y: null,
          plano_ancho: null,
          plano_alto: null,
          plano_radio: null,
          plano_vertices: null
        }
      }
      // Si tiene coordenadas de plano pero no de grid, limpiar grid
      else if (!tieneGridCoordinates && tieneMapCoordinates) {
        cleanForm = {
          ...cleanForm,
          grid_fila: null,
          grid_columna: null,
          grid_zona: ''
        }
      }

      // Si ubicacion está vacía o no es un WKT válido, no la enviamos al backend
      const ubicacionValida = form.ubicacion &&
        /^(POINT|POLYGON|LINESTRING|MULTIPOINT|MULTIPOLYGON|MULTILINESTRING|GEOMETRYCOLLECTION)/i.test(form.ubicacion.trim())
        ? form.ubicacion
        : null

      const formData = {
        ...cleanForm,
        ubicacion: ubicacionValida,
        imagenes: imagenesRelativas,
        tarifa_base_espacio_simple: form.tarifa_base_espacio_simple || null,
        tarifa_base_espacio_multiple: form.tarifa_base_espacio_multiple || null
      }

      if (editingEspacio) {
        await apiClient.put(`/api/admin-protected/espacios/${editingEspacio.id}`, formData)
      } else {
        await apiClient.post('/api/admin-protected/espacios', formData)
      }

      setShowForm(false)
      setEditingEspacio(null)
      resetForm()
      // Pequeña pausa para asegurar que la transacción se confirme
      await new Promise(resolve => setTimeout(resolve, 100))

      await fetchEspacios()
    } catch (err: any) {
      console.error('Error saving espacio:', err)
      setError(formatErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (espacio: Espacio) => {
    if (!hasPermission('espacios.editar')) {
      setError('No tienes permisos para editar espacios')
      return
    }

    setEditingEspacio(espacio)

    // Convertir URLs relativas a absolutas para las imágenes
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000'
    const imagenesCompletas = (espacio.imagenes || []).map(img =>
      img.startsWith('http') ? img : `${apiUrl}${img}`
    )

    // Validar que ubicacion sea un WKT string válido (debe empezar con POINT, POLYGON, etc.)
    // Si es un objeto geometry del backend, lo dejamos vacío
    const ubicacionValida = typeof espacio.ubicacion === 'string' &&
      /^(POINT|POLYGON|LINESTRING|MULTIPOINT|MULTIPOLYGON|MULTILINESTRING|GEOMETRYCOLLECTION)/i.test(espacio.ubicacion.trim())
      ? espacio.ubicacion
      : ''

    setForm({
      tipo_espacio_id: espacio.tipo_espacio_id,
      codigo: espacio.codigo,
      nombre: espacio.nombre,
      descripcion: espacio.descripcion || '',
      ubicacion: ubicacionValida,
      estado: espacio.estado,
      imagenes: imagenesCompletas,
      tarifa_base_espacio_simple: espacio.tarifa_base_espacio_simple,
      tarifa_base_espacio_multiple: espacio.tarifa_base_espacio_multiple,
      caracteristicas: espacio.caracteristicas || {},
      // Posición en cuadrícula
      grid_fila: espacio.grid_fila || null,
      grid_columna: espacio.grid_columna || null,
      grid_zona: espacio.grid_zona || '',
      // Posición en plano visual
      plano_x: espacio.plano_x || null,
      plano_y: espacio.plano_y || null,
      plano_ancho: espacio.plano_ancho || null,
      plano_alto: espacio.plano_alto || null,
      plano_radio: espacio.plano_radio || null,
      plano_vertices: espacio.plano_vertices || null
    })
    setShowForm(true)
  }

  const handleDelete = (id: string, nombre: string) => {
    if (!hasPermission('espacios.eliminar')) {
      setError('No tienes permisos para eliminar espacios')
      return
    }

    setItemToDelete({ id, nombre })
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return

    try {
      setLoading(true)
      setError(null)
      await apiClient.delete(`/api/admin-protected/espacios/${itemToDelete.id}`)
      setShowDeleteConfirm(false)
      setItemToDelete(null)
      await fetchEspacios()
    } catch (err: any) {
      console.error('Error deleting espacio:', err)
      setError(formatErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setForm({
      tipo_espacio_id: '',
      codigo: '',
      nombre: '',
      descripcion: '',
      ubicacion: '',
      estado: 'disponible',
      imagenes: [],
      tarifa_base_espacio_simple: null,
      tarifa_base_espacio_multiple: null,
      caracteristicas: {},
      // Posición en cuadrícula
      grid_fila: null,
      grid_columna: null,
      grid_zona: '',
      // Posición en plano visual
      plano_x: null,
      plano_y: null,
      plano_ancho: null,
      plano_alto: null,
      plano_radio: null,
      plano_vertices: null
    })
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingEspacio(null)
    resetForm()
  }

  const getEstadoColor = (estado: string) => {
    // Map estado values to status types for color mapping
    let statusType: 'available' | 'occupied' | 'maintenance' | 'pending' | 'cancelled' | 'confirmed' | 'completed' = 'pending'

    switch (estado) {
      case 'disponible':
        statusType = 'available'
        break
      case 'ocupado':
        statusType = 'occupied'
        break
      case 'mantenimiento':
        statusType = 'maintenance'
        break
      case 'inactivo':
        // Return custom styling for inactive state
        return {
          backgroundColor: '#F3F4F6',
          color: '#4B5563',
          borderColor: '#D1D5DB'
        }
    }

    const statusColors = getStatusColors(statusType, colors)
    return {
      backgroundColor: statusColors.backgroundColor,
      color: statusColors.textColor,
      borderColor: statusColors.borderColor
    }
  }

  if (!hasPermission('espacios.ver')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Sin permisos</h3>
          <p className="text-gray-600">No tienes permisos para ver los espacios</p>
        </div>
      </div>
    )
  }

  if (loading && espacios.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando espacios..." />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Gestión de Espacios</h2>
            <p className="text-gray-600">Administra los espacios de camping</p>
          </div>
          <div className="flex gap-3">
            {hasPermission('espacios.crear') && (
              <button
                onClick={() => setShowForm(true)}
                className="px-8 py-4 text-lg rounded-xl font-semibold text-white transition-all duration-300 ease-out cursor-pointer"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primaryDark
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary
                }}
              >
                ➕ Nuevo Espacio
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filtros</h3>
        <div className={`grid gap-4 ${
          deviceInfo.isMobile
            ? 'grid-cols-1'
            : 'grid-cols-2'
        }`}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Espacio
            </label>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
            >
              <option value="">Todos los tipos</option>
              {(tiposEspacios || []).map((tipo) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado
            </label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
            >
              <option value="">Todos los estados</option>
              <option value="disponible">Disponible</option>
              <option value="ocupado">Ocupado</option>
              <option value="mantenimiento">Mantenimiento</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          className="rounded-lg p-6 border"
          style={{
            backgroundColor: `${colors.error}15`,
            borderColor: colors.error
          }}
        >
          <div className="flex items-center">
            <span className="text-2xl mr-3">⚠️</span>
            <div>
              <h3 className="font-bold" style={{ color: colors.error }}>Error</h3>
              <p style={{ color: colors.error }}>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Espacios */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Espacios ({espacios.length})
          </h3>
        </div>

        {espacios.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🏕️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay espacios</h3>
            <p className="text-gray-600 mb-4">
              {hasPermission('espacios.crear') ? 'Crea el primer espacio para empezar' : 'No hay espacios disponibles'}
            </p>
            {hasPermission('espacios.crear') && (
              <button
                onClick={() => setShowForm(true)}
                className="px-8 py-4 text-lg rounded-xl font-semibold text-white transition-all duration-300 ease-out cursor-pointer"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primaryDark
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary
                }}
              >
                ➕ Crear Espacio
              </button>
            )}
          </div>
        ) : (
          <>
          {/* Table View (Desktop) */}
          {deviceInfo.isDesktop && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Espacio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarifa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Posición
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fotos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(espacios || []).map((espacio) => (
                  <tr key={espacio.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {espacio.nombre}
                        </div>
                        <div className="text-sm text-gray-500">
                          {espacio.codigo}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {espacio.tipo_espacio?.nombre || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border"
                        style={getEstadoColor(espacio.estado)}
                      >
                        {espacio.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {espacio.tarifa_base_espacio_simple
                          ? `$${espacio.tarifa_base_espacio_simple.toLocaleString()}`
                          : espacio.tipo_espacio?.tarifa_base_tipo
                          ? `$${espacio.tipo_espacio.tarifa_base_tipo.toLocaleString()}`
                          : 'N/A'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-900">
                        {espacio.grid_fila && espacio.grid_columna ? (
                          <div className="space-y-1">
                            <div>🎯 Fila {espacio.grid_fila}, Col {espacio.grid_columna}</div>
                            {espacio.grid_zona && <div>📍 {espacio.grid_zona}</div>}
                          </div>
                        ) : (
                          <span className="text-gray-400">Sin posición</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-900">
                        {espacio.imagenes && espacio.imagenes.length > 0 ? (
                          <div className="flex items-center space-x-1">
                            <span>📸</span>
                            <span>{espacio.imagenes.length} foto{espacio.imagenes.length > 1 ? 's' : ''}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">Sin fotos</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {hasPermission('espacios.editar') && (
                          <ActionButton
                            type="edit"
                            onClick={() => handleEdit(espacio)}
                            title="Editar espacio"
                          />
                        )}
                        {hasPermission('espacios.eliminar') && (
                          <ActionButton
                            type="delete"
                            onClick={() => handleDelete(espacio.id, espacio.nombre)}
                            title="Eliminar espacio"
                          />
                        )}
                        {!hasPermission('espacios.editar') && !hasPermission('espacios.eliminar') && (
                          <span className="text-gray-400 text-xs">Sin permisos</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* Cards View (Mobile/Tablet) */}
          {!deviceInfo.isDesktop && (
          <div className="space-y-4 p-6">
            {(espacios || []).map((espacio) => (
              <div key={espacio.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {espacio.nombre}
                    </h3>
                    <p className="text-xs text-gray-500">{espacio.codigo}</p>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Tipo</p>
                    <p className="text-sm font-medium text-gray-900">{espacio.tipo_espacio?.nombre || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Estado</p>
                    <span
                      className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border"
                      style={getEstadoColor(espacio.estado)}
                    >
                      {espacio.estado}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Tarifa</p>
                    <p className="text-sm font-medium text-gray-900">
                      ${(espacio.tarifa_base_espacio_simple || espacio.tipo_espacio?.tarifa_base_tipo || 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Fotos</p>
                    <p className="text-sm font-medium text-gray-900">{espacio.imagenes?.length || 0}</p>
                  </div>
                  {espacio.grid_fila && espacio.grid_columna && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 uppercase">Posición</p>
                      <p className="text-sm font-medium text-gray-900">
                        Fila {espacio.grid_fila}, Col {espacio.grid_columna} {espacio.grid_zona && `- ${espacio.grid_zona}`}
                      </p>
                    </div>
                  )}
                </div>

                {/* Acciones */}
                <div className="flex gap-1 pt-3 border-t border-gray-100">
                  {hasPermission('espacios.editar') && (
                    <ActionButton
                      type="edit"
                      onClick={() => handleEdit(espacio)}
                      title="Editar espacio"
                    />
                  )}
                  {hasPermission('espacios.eliminar') && (
                    <ActionButton
                      type="delete"
                      onClick={() => handleDelete(espacio.id, espacio.nombre)}
                      title="Eliminar espacio"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          )}
          </>
        )}
      </div>

      {/* Modal de Formulario */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-10 mx-auto p-4 sm:p-6 border w-full shadow-lg rounded-md bg-white max-h-screen overflow-y-auto ${
            deviceInfo.isMobile
              ? 'max-w-full mx-2'
              : deviceInfo.isTablet
              ? 'max-w-2xl'
              : 'max-w-4xl'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingEspacio ? 'Editar Espacio' : 'Nuevo Espacio'}
              </h3>
              <button
                onClick={closeForm}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className={`grid gap-4 ${
                deviceInfo.isMobile
                  ? 'grid-cols-1'
                  : 'grid-cols-2'
              }`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Espacio *
                  </label>
                  <select
                    required
                    value={form.tipo_espacio_id}
                    onChange={(e) => setForm({...form, tipo_espacio_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  >
                    <option value="">Seleccionar tipo</option>
                    {(tiposEspacios || []).map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.codigo}
                    onChange={(e) => setForm({...form, codigo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    placeholder="Ej: A01, B05"
                  />
                </div>

                <div className={deviceInfo.isMobile ? '' : 'col-span-2'}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.nombre}
                    onChange={(e) => setForm({...form, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    placeholder="Nombre del espacio"
                  />
                </div>

                <div className={deviceInfo.isMobile ? '' : 'col-span-2'}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                  </label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => setForm({...form, descripcion: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    placeholder="Descripción del espacio"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <select
                    value={form.estado}
                    onChange={(e) => setForm({...form, estado: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  >
                    <option value="disponible">Disponible</option>
                    <option value="ocupado">Ocupado</option>
                    <option value="mantenimiento">Mantenimiento</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tarifa Personalizada (1 unidad)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.tarifa_base_espacio_simple || ''}
                    onChange={(e) => setForm({...form, tarifa_base_espacio_simple: e.target.value ? parseFloat(e.target.value) : null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    placeholder="Deja vacío para usar tarifa base"
                  />
                  <p className="text-xs text-gray-500 mt-1">Tarifa para reservas de 1 unidad</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tarifa Personalizada (2+ unidades)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.tarifa_base_espacio_multiple || ''}
                    onChange={(e) => setForm({...form, tarifa_base_espacio_multiple: e.target.value ? parseFloat(e.target.value) : null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    placeholder="Deja vacío para usar tarifa simple"
                  />
                  <p className="text-xs text-gray-500 mt-1">Tarifa para reservas de 2 o más unidades (opcional)</p>
                </div>

                <div className={deviceInfo.isMobile ? '' : 'col-span-2'}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ubicación (WKT)
                  </label>
                  <input
                    type="text"
                    value={form.ubicacion}
                    onChange={(e) => setForm({...form, ubicacion: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    placeholder="Ej: POINT(-71.12345 -33.45678)"
                  />
                </div>
              </div>

              {/* Sección de Posición en Cuadrícula */}
              <div className="border-t pt-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">📍 Posición en Cuadrícula del Camping</h4>
                <div className={`grid gap-4 ${
                  deviceInfo.isMobile
                    ? 'grid-cols-1'
                    : deviceInfo.isTablet
                    ? 'grid-cols-2'
                    : 'grid-cols-3'
                }`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fila
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={form.grid_fila || ''}
                      onChange={(e) => setForm({...form, grid_fila: e.target.value ? parseInt(e.target.value) : null})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      placeholder="Ej: 1, 2, 3..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Columna
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={form.grid_columna || ''}
                      onChange={(e) => setForm({...form, grid_columna: e.target.value ? parseInt(e.target.value) : null})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      placeholder="Ej: A, B, C..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Zona
                    </label>
                    <input
                      type="text"
                      value={form.grid_zona}
                      onChange={(e) => setForm({...form, grid_zona: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      placeholder="Ej: Norte, Sur, Río"
                    />
                  </div>
                </div>
              </div>

              {/* Sección de Posición en Plano Visual con Mapeador Interactivo */}
              <PlanoMapper
                planoImageUrl={`${import.meta.env.VITE_API_URL}/static/uploads/planos/plano-camping.png`}
                espacioCodigo={form.codigo || 'Sin código'}
                initialValues={{
                  plano_x: form.plano_x,
                  plano_y: form.plano_y,
                  plano_radius: form.plano_radio,
                  plano_ancho: form.plano_ancho,
                  plano_alto: form.plano_alto,
                  plano_vertices: form.plano_vertices
                }}
                onCoordinatesChange={handleCoordinatesChange}
              />

              {/* Sección de Fotos */}
              <div className="border-t pt-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">📸 Fotos del Espacio</h4>
                {editingEspacio ? (
                  <ImageUploader
                    mode="multiple"
                    images={(editingEspacio.imagenes || []).filter((img: string) => img && img.trim() !== '')}
                    uploadEndpoint={`/api/admin-protected/espacios/${editingEspacio.id}/upload-images`}
                    onImagesUpdated={async () => {
                      // Refrescar la lista de espacios
                      await fetchEspacios()
                      // Actualizar el editingEspacio con datos frescos
                      try {
                        const response = await apiClient.get(`/api/admin-protected/espacios`)
                        const espacios = response.data || []
                        const espacioActualizado = espacios.find((e: Espacio) => e.id === editingEspacio.id)
                        if (espacioActualizado) {
                          // Filtrar imágenes vacías antes de actualizar
                          let imagenesActualizadas = espacioActualizado.imagenes || []
                          if (imagenesActualizadas) {
                            imagenesActualizadas = imagenesActualizadas.filter((img: string) => img && img.trim() !== '')
                          }

                          espacioActualizado.imagenes = imagenesActualizadas
                          setEditingEspacio(espacioActualizado)

                          // IMPORTANTE: Actualizar también el form para que el submit tenga los datos correctos
                          setForm(prev => ({
                            ...prev,
                            imagenes: imagenesActualizadas
                          }))
                        }
                      } catch (error) {
                        console.error('Error refrescando espacio:', error)
                      }
                    }}
                    maxImages={10}
                  />
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      💡 Primero guarda el espacio para poder agregar fotos
                    </p>
                  </div>
                )}
              </div>

              {/* Tarifas Personalizadas - Solo mostrar cuando se está editando */}
              {editingEspacio && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <EspacioTarifasManager
                    espacioId={editingEspacio.id}
                    empresaId={usuario?.empresa_activa?.id || 0}
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-6 py-3 text-base rounded-lg font-semibold transition-all duration-300 ease-out cursor-pointer"
                  style={{
                    backgroundColor: '#f3f4f6',
                    color: colors.primary,
                    border: `1px solid #d1d5db`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e5e7eb'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6'
                  }}
                >
                  ❌ Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 text-base rounded-lg font-semibold text-white transition-all duration-300 ease-out cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: colors.primary
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = colors.primaryDark
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = colors.primary
                  }}
                >
                  {loading ? '💾 Guardando...' : editingEspacio ? '✏️ Actualizar' : '➕ Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
        )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        title="¿Eliminar espacio?"
        message="¿Estás seguro que deseas eliminar este espacio?"
        itemName={itemToDelete?.nombre}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false)
          setItemToDelete(null)
        }}
        isLoading={loading}
      />
    </div>
  )
}

export default AdminEspacios